import { SoulEntry, EntryTake, EmotionMood, DialogueLine } from '../types';

export interface CreateEntryPayload {
  title: string;
  performerName: string;
  clipId: string;
  videoUrl: string;
  bgmUrl?: string;
  vocalsUrl?: string;
  posterUrl?: string;
  moodTag?: EmotionMood;
  trimStart?: number;
  trimEnd?: number;
  duration: number;
  dialogueLines?: DialogueLine[];
}

export interface UpdateEntryPayload {
  title?: string;
  performerName?: string;
  moodTag?: EmotionMood;
  dialogueLines?: DialogueLine[];
  status?: 'draft' | 'shared';
}

export interface UploadTakeParams {
  entryId: string;
  clipId?: string;
  performerName: string;
  audioBlob: Blob;
  duration: number;
  lineIds: string[];
  lineStart: number;
  lineEnd: number;
  characterLabel?: string;
  takeNumber?: number;
}

/**
 * Fetch a single entry by ID, including all its recorded takes.
 */
export async function fetchEntry(id: string): Promise<SoulEntry | null> {
  try {
    const res = await fetch(`/api/entry?id=${encodeURIComponent(id)}`);
    if (!res.ok) {
      if (res.status === 404) return null;
      throw new Error(`Failed to fetch entry: ${res.statusText}`);
    }
    const data = await res.json() as { success: boolean; entry?: SoulEntry; error?: string };
    if (!data.success || !data.entry) {
      return null;
    }
    return data.entry;
  } catch (err) {
    console.error('Error fetching entry:', err);
    return null;
  }
}

/**
 * Fetch the latest entry associated with a specific clip ID, including all its recorded takes.
 */
export async function fetchEntryByClipId(clipId: string, userName?: string): Promise<SoulEntry | null> {
  try {
    const url = userName?.trim()
      ? `/api/entry?clipId=${encodeURIComponent(clipId)}&user=${encodeURIComponent(userName.trim())}`
      : `/api/entry?clipId=${encodeURIComponent(clipId)}`;
    console.log('[fetchEntryByClipId] Requesting entry for clipId:', clipId, 'user:', userName);
    const res = await fetch(url);
    if (!res.ok) {
      console.log('[fetchEntryByClipId] HTTP error:', res.status);
      if (res.status === 404) return null;
      return null;
    }
    const data = await res.json() as { success: boolean; entry?: SoulEntry | null; error?: string };
    console.log('[fetchEntryByClipId] Response:', 
      `success=${data.success}, hasEntry=${!!data.entry}, entryId=${data.entry?.id}, clipId=${data.entry?.clipId}, takeCount=${data.entry?.takes?.length}, updatedAt=${data.entry?.updatedAt}`,
      'takes=', data.entry?.takes?.map((t: any) => ({ id: t.id, entryId: t.entryId })));
    if (!data.success || !data.entry) {
      return null;
    }
    return data.entry;
  } catch (err) {
    console.warn('Error fetching entry by clipId:', err);
    return null;
  }
}

/**
 * List recent entries from the database, optionally filtered by user.
 */
export async function listEntries(userName?: string): Promise<SoulEntry[]> {
  try {
    const url = userName?.trim()
      ? `/api/entry?user=${encodeURIComponent(userName.trim())}`
      : '/api/entry';
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to list entries: ${res.statusText}`);
    const data = await res.json() as { success: boolean; entries?: SoulEntry[]; error?: string };
    return data.entries || [];
  } catch (err) {
    console.error('Error listing entries:', err);
    return [];
  }
}

/**
 * Create a new SoulSync Entry.
 */
export async function createEntry(payload: CreateEntryPayload): Promise<SoulEntry> {
  console.log('[createEntry] Creating entry for clipId:', payload.clipId);
  const res = await fetch('/api/entry', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to create entry: ${errorText || res.statusText}`);
  }

  const data = await res.json() as { success: boolean; entry: SoulEntry; error?: string };
    console.log('[createEntry] Response:', `success=${data.success}, entryId=${data.entry?.id}, clipId=${data.entry?.clipId}`);
  if (!data.success || !data.entry) {
    throw new Error(data.error || 'Failed to create entry');
  }

  return data.entry;
}

/**
 * Update an existing entry's metadata or dialogue line definitions.
 */
export async function updateEntry(id: string, updates: UpdateEntryPayload): Promise<SoulEntry> {
  const res = await fetch(`/api/entry?id=${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates)
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to update entry: ${errorText || res.statusText}`);
  }

  const data = await res.json() as { success: boolean; entry: SoulEntry; error?: string };
  if (!data.success || !data.entry) {
    throw new Error(data.error || 'Failed to update entry');
  }

  return data.entry;
}

/**
 * Fork an existing entry for collaboration.
 * Duplicates the entry and all its takes under a new ID.
 */
export async function forkEntry(id: string, performerName: string): Promise<SoulEntry> {
  const res = await fetch(`/api/entry?action=fork&id=${encodeURIComponent(id)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ performerName })
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to fork entry: ${errorText || res.statusText}`);
  }

  const data = await res.json() as { success: boolean; entry: SoulEntry; forkedFrom?: string; error?: string };
  if (!data.success || !data.entry) {
    throw new Error(data.error || 'Failed to fork entry');
  }

  return data.entry;
}

/**
 * Mark an entry as shared and get the canonical share URL.
 */
export async function shareEntry(id: string): Promise<{ shareUrl: string; entryId: string }> {
  const res = await fetch(`/api/entry?action=share&id=${encodeURIComponent(id)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to share entry: ${errorText || res.statusText}`);
  }

  const data = await res.json() as { success: boolean; shareUrl: string; entryId: string; error?: string };
  if (!data.success) {
    throw new Error(data.error || 'Failed to share entry');
  }

  return { shareUrl: data.shareUrl, entryId: data.entryId };
}

/**
 * Delete an entry and all its associated takes and R2 media.
 */
export async function deleteEntry(id: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/entry?id=${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
    if (!res.ok) return false;
    const data = await res.json() as { success: boolean };
    return !!data.success;
  } catch (err) {
    console.error('Error deleting entry:', err);
    return false;
  }
}

/**
 * Upload a newly recorded audio take for an entry line/scene.
 * Streams the binary blob to R2 and writes record in D1.
 */
export async function uploadEntryTake(params: UploadTakeParams): Promise<EntryTake> {
  console.log('[uploadEntryTake] Uploading take for entryId:', params.entryId, 'lineIds:', JSON.stringify(params.lineIds));
  const headers: Record<string, string> = {
    'Content-Type': params.audioBlob.type || 'audio/webm',
    'x-entry-id': params.entryId,
    ...(params.clipId ? { 'x-clip-id': params.clipId } : {}),
    'x-performer-name': params.performerName || 'Anonymous',
    'x-line-ids': JSON.stringify(params.lineIds || []),
    'x-line-start': String(params.lineStart ?? 0),
    'x-line-end': String(params.lineEnd ?? 0),
    'x-duration': String(params.duration ?? 0),
    'x-character-label': params.characterLabel || '',
    'x-take-number': String(params.takeNumber || 1),
    'x-mime-type': params.audioBlob.type || 'audio/webm'
  };

  const res = await fetch('/api/entry-take', {
    method: 'POST',
    headers,
    body: params.audioBlob
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to upload take: ${errorText || res.statusText}`);
  }

  const data = await res.json() as { success: boolean; take: EntryTake; error?: string };
  if (!data.success || !data.take) {
    throw new Error(data.error || 'Failed to upload take');
  }

  return data.take;
}

/**
 * Delete an entry take and clean up its R2 asset.
 */
export async function deleteEntryTake(takeId: string, entryId: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/entry-take?takeId=${encodeURIComponent(takeId)}&id=${encodeURIComponent(takeId)}&entryId=${encodeURIComponent(entryId)}`, {
      method: 'DELETE'
    });
    if (!res.ok) return false;
    const data = await res.json() as { success: boolean };
    return !!data.success;
  } catch (err) {
    console.error('Error deleting take:', err);
    return false;
  }
}

/**
 * Persist take sync timing adjustments (lineStart, lineEnd) to D1.
 */
export async function updateTakeTiming(takeId: string, lineStart: number, lineEnd: number): Promise<boolean> {
  try {
    const res = await fetch('/api/entry-take', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ takeId, lineStart, lineEnd })
    });
    if (!res.ok) return false;
    const data = await res.json() as { success: boolean };
    return !!data.success;
  } catch (err) {
    console.warn('Error updating take timing in D1:', err);
    return false;
  }
}

/**
 * Persist take playback volume level to D1.
 */
export async function updateTakeVolume(takeId: string, volume: number): Promise<boolean> {
  try {
    const res = await fetch('/api/entry-take', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ takeId, volume })
    });
    if (!res.ok) return false;
    const data = await res.json() as { success: boolean };
    return !!data.success;
  } catch (err) {
    console.warn('Error updating take volume in D1:', err);
    return false;
  }
}

